import React from 'react';
import { CheckCircle, X } from 'lucide-react';
import { Button } from './ui/button';

export interface SessionSummaryData {
  sessionName: string;
  duration: string;

  // Transcripts
  totalTranscripts: number;
  salespersonTranscripts: number;
  customerTranscripts: number;

  // Landmines
  totalLandmines: number;
  salespersonLandmines: number;
  customerLandmines: number;

  // Competitors
  totalCompetitors: number;
  salespersonCompetitors: number;
  customerCompetitors: number;

  // Questions by category
  objections: number;
  faqs: number;
  newInquiries: number;

}

interface SessionSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClearSession: () => void;
  summary: SessionSummaryData;
}

const SessionSummaryModal: React.FC<SessionSummaryModalProps> = ({
  isOpen,
  onClose,
  onClearSession,
  summary,
}) => {
  if (!isOpen) return null;

  const StatRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-ink-secondary">{label}</span>
      <span className="text-sm font-semibold text-ink">{value}</span>
    </div>
  );

  const TotalItem: React.FC<{ value: number; label: string }> = ({ value, label }) => (
    <div className="flex items-center gap-1">
      <span className="text-xs font-semibold text-ink-secondary">{value}</span>
      <span className="text-xs text-ink-muted">{label}</span>
    </div>
  );

  return (
    <div className="modal-backdrop">
      <div className="modal-panel w-[420px] overflow-hidden">
        {/* Header */}
        <div className="bg-success px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Session Complete
              </h2>
              <div className="flex items-center text-sm text-white/80 mt-1">
                <span>{summary.sessionName}</span>
                <span className="mx-2 text-white/60">|</span>
                <span>{summary.duration}</span>
              </div>
            </div>
            <Button
              variant="tertiary"
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Body: Two Columns */}
        <div className="px-5 py-4">
          <div className="flex gap-4">
            {/* Left Column: Salesperson */}
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-ink mb-3">Salesperson</h3>
              <div className="space-y-1">
                <StatRow label="Transcripts" value={summary.salespersonTranscripts} />
                <StatRow label="Landmine Triggers" value={summary.salespersonLandmines} />
                <StatRow label="Competitor Mentions" value={summary.salespersonCompetitors} />
              </div>
            </div>

            {/* Divider */}
            <div className="w-px bg-surface-sunken" />

            {/* Right Column: Customer */}
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-ink mb-3">Customer</h3>
              <div className="space-y-1">
                <StatRow label="Transcripts" value={summary.customerTranscripts} />
                <StatRow label="Landmine Triggers" value={summary.customerLandmines} />
                <StatRow label="Competitor Mentions" value={summary.customerCompetitors} />
                <StatRow label="Objections" value={summary.objections} />
                <StatRow label="FAQs" value={summary.faqs} />
                <StatRow label="New Inquiries" value={summary.newInquiries} />
              </div>
            </div>
          </div>
        </div>

        {/* Totals Row */}
        <div className="border-t border-hairline px-5 py-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-ink-muted uppercase">Totals</span>
            <div className="flex items-center gap-4">
              <TotalItem value={summary.totalTranscripts} label="transcripts" />
              <TotalItem value={summary.totalLandmines} label="landmines" />
              <TotalItem value={summary.totalCompetitors} label="competitors" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-surface-subtle px-5 py-3 border-t border-hairline flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="destructive" onClick={onClearSession}>
            Clear Session Data
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionSummaryModal;
